import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type {
    UpdateVesselTypesRequest,
    VesselTypes,
    VesselTypesRequest,
    VesselTypesResponse,
    VesselTypesUpdateResponse,
} from '@routes/sidebar/admin-center/___shared___/api/vesselTypes.api.ts';
import {
    getApiVesselTypes,
    updateApiVesselTypes,
} from '@routes/sidebar/admin-center/___shared___/api/vesselTypes.api.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import { extractError } from '@utils/slice.ts';

export interface VesselTypesError {
    message?: string;
    [k: string]: any;
}

export interface VesselTypesState {
    error: VesselTypesError | null;

    vesselTypesCommon: VesselTypesResponse | null; // use for common data just in case
    vesselTypes: VesselTypes[];
    pagination: CommonTablePaginatedResponse;

    updatedVesselType: VesselTypes | null;
    currentGetRequestId: string | null;

    isGetVesselTypesPending: boolean;
    isUpdateVesselTypesPending: boolean;
}

const initialState: VesselTypesState = {
    error: null,

    vesselTypesCommon: null,
    vesselTypes: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    updatedVesselType: null,
    currentGetRequestId: null,

    isGetVesselTypesPending: false,
    isUpdateVesselTypesPending: false,
};

export const getVesselTypes = createAsyncThunk<
    VesselTypesResponse,
    { clientId: number; tenantAlias: string; payload: VesselTypesRequest },
    { rejectValue: VesselTypesError }
>('adminCenterVesselTypes/getApiVesselTypes', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiVesselTypes(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateVesselTypes = createAsyncThunk<
    VesselTypesUpdateResponse,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateVesselTypesRequest },
    { rejectValue: VesselTypesError }
>('adminCenterVesselTypes/updateApiVesselTypes', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiVesselTypes(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const vesselTypesSlice = createSlice({
    name: 'adminCenterVesselTypes',
    initialState,
    reducers: {
        clearVesselTypesState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getVesselTypes.pending, (state, action) => {
            state.isGetVesselTypesPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getVesselTypes.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselTypesPending = false;
            state.currentGetRequestId = null;

            const { data, pagination } = action.payload;
            state.vesselTypesCommon = action.payload;
            state.vesselTypes = data;
            state.pagination = pagination;
        });
        builder.addCase(getVesselTypes.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselTypesPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(updateVesselTypes.pending, (state) => {
            state.isUpdateVesselTypesPending = true;
            state.error = null;
        });
        builder.addCase(updateVesselTypes.fulfilled, (state, action) => {
            state.isUpdateVesselTypesPending = false;
            state.updatedVesselType = action.payload;
        });
        builder.addCase(updateVesselTypes.rejected, (state, action) => {
            state.isUpdateVesselTypesPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { vesselSubTypeResponse } = action.payload;

            if (!vesselSubTypeResponse) return;

            const { data, pagination } = vesselSubTypeResponse;
            state.vesselTypesCommon = vesselSubTypeResponse;
            state.vesselTypes = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const { clearVesselTypesState } = vesselTypesSlice.actions;
export default vesselTypesSlice.reducer;
